import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import SponsorLogoUpload from '../sponsors/SponsorLogoUpload';

const SponsorItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  background: #f8f9fa;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SponsorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SponsorLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: contain;
  background: white;
`;

function UpdateLivestreamModal({ show, onHide, initialData, onUpdated }) {
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [price, setPrice] = useState('');
  const [logoType, setLogoType] = useState('');
  const [sponsors, setSponsors] = useState([]);
  const [tempSponsorName, setTempSponsorName] = useState('');
  const [error, setError] = useState(null);

  // Fill state when initialData changes
  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description || '');
      setStartTime(initialData.startTime || '');
      setEndTime(initialData.endTime || '');
      setPrice(initialData.price || '');
      setLogoType(initialData.logoType || '');
      setSponsors(initialData.sponsors || []);
    }
  }, [initialData]);

  const handleAddSponsor = () => {
    if (!tempSponsorName.trim()) {
      toast.warning('Please enter a sponsor name');
      return;
    }

    const newSponsor = {
      sponsorName: tempSponsorName.trim(),
      logoUrl: '' // Will be updated when logo is uploaded
    };

    setSponsors(prev => [...prev, newSponsor]);
    setTempSponsorName('');
  };

  const handleRemoveSponsor = (index) => {
    setSponsors(prev => prev.filter((_, i) => i !== index));
  };

  const handleLogoUpload = (index, logoUrl) => {
    setSponsors(prev => prev.map((sponsor, i) => 
      i === index ? { ...sponsor, logoUrl } : sponsor
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const updatedLivestream = {
      id: initialData.id,
      description,
      startTime,
      endTime,
      price,
      logoType,
      sponsors,
    };

    try {
      console.log('[UpdateLivestreamModal] Submitting update with data:', updatedLivestream);
      const response = await api.put(`/livestream/${initialData.id}`, updatedLivestream);
      console.log('[UpdateLivestreamModal] Update response received:', response);
      console.log('[UpdateLivestreamModal] Updated livestream object:', response.data);
      
      onUpdated(response.data);
      toast.success('Livestream updated successfully');
      onHide();
    } catch (err) {
      console.error('[UpdateLivestreamModal] Update failed:', err);
      setError('Update failed. Please try again.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Livestream</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{typeof error === 'string' ? error : error?.message || 'An error occurred'}</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Form.Group controlId="formDescription" className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Form.Group>

          <div className="row">
            <Form.Group controlId="formStartTime" className="col-md-6 mb-3">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formEndTime" className="col-md-6 mb-3">
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </Form.Group>
          </div>

          <div className="row">
            <Form.Group controlId="formPrice" className="col-md-6 mb-3">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="formLogoType" className="col-md-6 mb-3">
              <Form.Label>Logo Type</Form.Label>
              <Form.Control
                type="text"
                value={logoType}
                onChange={(e) => setLogoType(e.target.value)}
                required
              />
            </Form.Group>
          </div>

          {/* Sponsors Section */}
          <Form.Group className="mb-4">
            <Form.Label>Sponsors</Form.Label>
            
            {/* Add new sponsor */}
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                type="text"
                placeholder="Sponsor Name"
                value={tempSponsorName}
                onChange={(e) => setTempSponsorName(e.target.value)}
              />
              <Button variant="secondary" onClick={handleAddSponsor}>
                Add Sponsor
              </Button>
            </div>

            {/* List of sponsors */}
            {sponsors.map((sponsor, index) => (
              <SponsorItem key={index}>
                <SponsorInfo>
                  {sponsor.logoUrl && (
                    <SponsorLogo src={sponsor.logoUrl} alt={sponsor.sponsorName} />
                  )}
                  <div>
                    <strong>{sponsor.sponsorName}</strong>
                    {!sponsor.logoUrl && (
                      <div className="mt-2">
                        <SponsorLogoUpload
                          onUploadComplete={(logoUrl) => handleLogoUpload(index, logoUrl)}
                        />
                      </div>
                    )}
                  </div>
                </SponsorInfo>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveSponsor(index)}
                >
                  Remove
                </Button>
              </SponsorItem>
            ))}
          </Form.Group>

          <Button variant="primary" type="submit" className="mt-4">
            Update Livestream
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default UpdateLivestreamModal;
